import {createSelector} from 'reselect';

import MAP_TYPE from './enums/mapType';

const deviceSelector = (state) => state?.device || {};
const eventSelector = (state) => state?.event || {};
const eventRoyalSeatmapSelector = (state) => state?.eventPageRoyalSeatmap || {};
const ticketListSelector = (state) => state?.ticketList || {};

const deviceTypeSelector = createSelector(deviceSelector, (device = {}) => device && device.type);
const venueConfigSelector = createSelector(eventSelector, (event) => {
  return event?.venueConfig || {};
});

const dataSelector = createSelector(venueConfigSelector, (venueConfig) => {
  const {id: venueConfigId} = venueConfig;

  return {
    venueConfig,
    venueConfigId,
  };
});

const mapSectionStatsSelector = createSelector(
  eventRoyalSeatmapSelector,
  (eventPageRoyalSeatmap) => eventPageRoyalSeatmap.mapSectionStats || []
);
const mapZoneStatsSelector = createSelector(
  eventRoyalSeatmapSelector,
  (eventPageRoyalSeatmap) => eventPageRoyalSeatmap.mapZoneStats || []
);
const sectionZoneMetasSelector = createSelector(
  venueConfigSelector,
  (venueConfig) => venueConfig.sectionZoneMetas || {}
);

const availableSectionsSelector = createSelector(
  mapSectionStatsSelector,
  mapZoneStatsSelector,
  sectionZoneMetasSelector,
  (_, {mapType}) => mapType || MAP_TYPE.SECTION,
  (_, {listingInfo}) => listingInfo,
  (mapSectionStats, mapZoneStats, sectionZoneMetas, mapType, listingInfo) => {
    const allMapSections = Object.keys(sectionZoneMetas);

    let availableSections = [];
    let availableZonesSections = {};
    if (mapType === MAP_TYPE.SECTION && !listingInfo) {
      availableSections = getActiveSectionIdsAsArray(mapSectionStats);
    } else if (mapType === MAP_TYPE.ZONE || listingInfo) {
      const availableZones = mapZoneStats
        .filter((zone) => zone && typeof zone.zoneId !== 'undefined' && zone.zoneId !== null)
        .map((zone) => zone.zoneId?.toString());

      availableSections = Object.keys(sectionZoneMetas).filter((sectionId) =>
        availableZones.includes(sectionZoneMetas[sectionId].zi?.toString())
      );
    }

    availableZonesSections = mapZoneStats.reduce((lookup, zone) => {
      lookup[zone.zoneId] = allMapSections.filter(
        (sectionId) => sectionZoneMetas[sectionId]?.zi?.toString() === zone.zoneId?.toString()
      );

      return lookup;
    }, {});

    const availableSectionsLookUp = {};
    for (let i = 0; i < availableSections.length; i++) {
      availableSectionsLookUp[availableSections[i]] = true;
    }

    return {
      availableSections,
      availableSectionsLookUp,
      availableZonesSections,
    };
  }
);

function getActiveSectionIdsAsArray(sectionStats = []) {
  return sectionStats
    .filter((section) => section && typeof section.sectionId !== 'undefined' && section.sectionId !== null)
    .map(({sectionId}) => String(sectionId));
}

const sectionColorsSelector = createSelector(sectionZoneMetasSelector, (sectionZoneMetas) => {
  const allMapSections = Object.keys(sectionZoneMetas);

  return allMapSections.reduce((colorMap, section) => {
    const sectionColor = sectionZoneMetas[section].c || null;

    return {
      [section]: typeof sectionColor === 'string' ? sectionColor : null,
      ...colorMap,
    };
  }, {});
});

const previousListingSelectedSelector = createSelector(
  eventRoyalSeatmapSelector,
  (eventPageRoyalSeatmap = {}) => eventPageRoyalSeatmap.previousListingSelected || null
);

const previousBYOQuantitySelector = createSelector(
  eventRoyalSeatmapSelector,
  (eventPageRoyalSeatmap = {}) => eventPageRoyalSeatmap.previousBYOQuantity || null
);

const listingSelectedSelector = createSelector(eventSelector, ({listingSelected}) => listingSelected);

const filterChangedSelector = createSelector(eventSelector, ({filterChanged}) => filterChanged);

const rowListingSelectedSelector = createSelector(eventSelector, ({rowListingSelected}) => rowListingSelected);

const hoveredListingSectionIdSelector = createSelector(
  ticketListSelector,
  ({hoveredListingSid}) => hoveredListingSid || null
);

const hoveredListingZoneIdSelector = createSelector(
  ticketListSelector,
  ({hoveredListingZid}) => hoveredListingZid || null
);

const gsConfigSelector = (state) => (state && state.gsConfig) || {};
const storeIdSelector = createSelector(gsConfigSelector, (gsConfig) => gsConfig.shstoreId);

export {
  deviceTypeSelector,
  dataSelector,
  availableSectionsSelector,
  sectionColorsSelector,
  previousListingSelectedSelector,
  previousBYOQuantitySelector,
  listingSelectedSelector,
  filterChangedSelector,
  rowListingSelectedSelector,
  hoveredListingSectionIdSelector,
  hoveredListingZoneIdSelector,
  storeIdSelector,
};
